// =====================================================================================
// INVRT Radar — web console — account — delivery — the event-kind chooser
//
// One labelled checkbox per kind in the closed vocabulary (hooks.ts EVENT_KIND_OPTIONS),
// used by the webhook "Add endpoint" dialog, the target form and the target edit dialog.
// The value handed back is always in vocabulary order, whatever order the boxes were
// ticked in, so the request body is deterministic.
// =====================================================================================

import { useId } from "react";
import type { WebhookEventKind } from "@/lib/types.c1";
import { EVENT_KIND_OPTIONS } from "./hooks";

export interface EventKindCheckboxesProps {
  legend: string;
  hint?: string;
  value: WebhookEventKind[];
  onChange: (next: WebhookEventKind[]) => void;
  disabled?: boolean;
}

export function EventKindCheckboxes({
  legend,
  hint,
  value,
  onChange,
  disabled = false,
}: EventKindCheckboxesProps) {
  const base = useId();

  function toggle(kind: WebhookEventKind, checked: boolean): void {
    const selected = new Set(value);
    if (checked) selected.add(kind);
    else selected.delete(kind);
    onChange(EVENT_KIND_OPTIONS.map((option) => option.kind).filter((k) => selected.has(k)));
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-ink">{legend}</legend>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
      {EVENT_KIND_OPTIONS.map((option) => {
        const id = `${base}-${option.kind}`;
        return (
          <label key={option.kind} htmlFor={id} className="flex items-start gap-2 text-sm">
            <input
              id={id}
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary"
              checked={value.includes(option.kind)}
              disabled={disabled}
              onChange={(event) => toggle(option.kind, event.target.checked)}
            />
            <span>
              <span className="font-medium text-ink">{option.label}</span>
              <span className="block text-xs text-ink-muted">{option.description}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
